import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCmsDoc } from '../../hooks/useCms';
import { FALLBACK_HERO, heroT } from '../../data/cmsFallbacks';

const HeroSection = () => {
  const videoRef = useRef(null);
  const { t } = useLanguage();
  const hero = useCmsDoc('/api/cms/hero', FALLBACK_HERO);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.85;
    }
  }, []);

  return (
    <section 
      className="relative h-screen w-full overflow-hidden" 
      data-testid="hero-section"
    >
      {/* Full-screen Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          key={hero.video_url}
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={hero.video_url || FALLBACK_HERO.video_url} type="video/mp4" />
        </video>
        {/* Navy gradient from left to transparent on right - smoother fade */}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(to right, #003366 0%, #003366 20%, rgba(0,51,102,0.8) 40%, rgba(0,51,102,0.4) 60%, transparent 85%)'
        }} />
      </div>

      {/* Content Overlay - Left aligned */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full pl-[5%] md:pl-[8%] pr-4 md:pr-6">
          <div className="max-w-2xl">
            {/* Headlines - 2 lines */}
            <div className="flex flex-col" style={{ width: 'fit-content' }}>
              <h1 
                className="font-bold text-white leading-[1.15] mb-1 animate-fade-in-up md:whitespace-nowrap"
                style={{ fontSize: 'clamp(1.7rem, 3.9vw, 2.68rem)' }}
              >
                {t('headline', heroT(hero))}
              </h1>
              <h2 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 animate-fade-in-up animation-delay-100 md:whitespace-nowrap"
              >
                <span className="text-white">
                  {t('subheadline', heroT(hero))}
                </span>
              </h2>

              {/* Golden Glow Line Separator - same width as Personal-Lösungen */}
              <div className="relative h-[2px] bg-black overflow-hidden mb-4 md:mb-6" style={{ width: '100%' }}>
                <div 
                  className="absolute top-0 h-full w-[150px]"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                    animation: 'heroGlowLine 3s ease-in-out infinite',
                    boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
                  }}
                />
              </div>
            </div>

            {/* Description */}
            <p className="text-white/70 text-sm md:text-base lg:text-lg mb-6 md:mb-8 animate-fade-in-up animation-delay-200 md:whitespace-nowrap">
              {t('description', heroT(hero))}
            </p>

            {/* CTA Button */}
            <div className="animate-fade-in-up animation-delay-300">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-2 md:gap-3 px-5 py-3 md:px-8 md:py-4 bg-[#D4AF37] text-[#003366] font-semibold text-sm md:text-base rounded-md border border-[#B8960C] overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-[#D4AF37]/30 hover:bg-[#C9A227] hover:scale-105"
                data-testid="hero-cta-btn"
              >
                <span>{t('ctaText', heroT(hero))}</span>
                <ArrowRight size={16} className="md:w-[18px] md:h-[18px] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Center - "Unsere Dienstleistungen" with scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center text-center">
        <h3 className="text-white font-bold text-lg md:text-xl mb-1">
          {t('ourServices', { de: 'Unsere Dienstleistungen', en: 'Our Services' })}
        </h3>
        <p className="text-white/60 text-sm mb-3">
          {t('weOffer', { de: 'Wir bieten', en: 'We offer' })}
        </p>
        <div className="animate-bounce">
          <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

      {/* Animated Golden Glow Line at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black overflow-hidden z-30">
        <div 
          className="absolute top-0 h-full w-[300px]"
          style={{
            background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
            animation: 'heroGlowLine 4s ease-in-out infinite',
            boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
          }}
        />
      </div>

      {/* Glow Line Animation */}
      <style>{`
        @keyframes heroGlowLine {
          0% {
            left: -300px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
